// 현장 상세 공정 차트·진행 스트립 계산 — 날짜를 「범위 첫날부터 몇째 칸」 으로 바꾼다(화면은 칸만 그린다).

import type { ChangeOrder } from "../change-order/types";
import type { ContractPayment } from "../contract/rows";
import {
  countsPhaseDelay,
  DELAY_KINDS,
  delayedPhaseCount,
  phaseSchedule,
  phaseScheduleText,
  scheduleToneKind,
  type PhaseSchedule,
  type PhaseScheduleKind,
} from "./phase-schedule";
import type { Project, ProjectPhase, ProjectPhaseStatus, ProjectStatus } from "./types";
import { dDayLabel, dDayOf } from "./view";

const DAY_MS = 86_400_000;

const toMs = (date: string): number => Date.parse(`${date}T00:00:00Z`);

export const daysBetween = (from: string, to: string): number => Math.round((toMs(to) - toMs(from)) / DAY_MS);

export const addDays = (date: string, days: number): string =>
  new Date(toMs(date) + days * DAY_MS).toISOString().slice(0, 10);

// 차트·달력 `?from=`·`?month=` 가 받는 달 — 1900~2199 년만(9999-10 은 창 끝이 10000 년이라 날짜 계산이 죽는다).
const BOARD_MONTH_RE = /^(?:19|20|21)\d{2}-(?:0[1-9]|1[0-2])$/;

export const isBoardMonth = (value: string | null | undefined): value is string =>
  typeof value === "string" && BOARD_MONTH_RE.test(value);

// `YYYY-MM` 에서 n 달 앞뒤 — 음수도 받는다.
export const shiftMonth = (month: string, delta: number): string => {
  const m = Number(month.slice(5, 7)) - 1 + delta;
  const year = Number(month.slice(0, 4)) + Math.floor(m / 12);
  return `${String(year).padStart(4, "0")}-${String((((m % 12) + 12) % 12) + 1).padStart(2, "0")}`;
};

const minOf = (dates: readonly string[]): string => dates.reduce((a, b) => (b < a ? b : a));
const maxOf = (dates: readonly string[]): string => dates.reduce((a, b) => (b > a ? b : a));

export type ChartPhase = Pick<
  ProjectPhase,
  | "id"
  | "phase_code"
  | "status"
  | "start_date"
  | "end_date"
  | "actual_start_date"
  | "actual_end_date"
  | "sort_order"
>;

export interface ChartRange {
  start: string;
  end: string;
  // 칸 수(첫날·끝날 포함).
  days: number;
}

// 막대 한 토막 — from 은 0 부터 세는 칸, span 은 칸 수(1 이상).
export interface ChartSpan {
  from: number;
  span: number;
}

export interface ChartRow {
  id: string;
  phaseCode: string;
  status: ProjectPhaseStatus;
  startDate: string;
  endDate: string;
  plan: ChartSpan;
  // 실제 — 착수 기록이 있을 때만. 진행·보류 중이면 오늘까지.
  actual: ChartSpan | null;
  ongoing: boolean;
  // 넘긴 꼬리(overdueSpanOf) — 지연은 끝날 다음 날, 끝날 지난 착수 지연은 시작일 다음 날 ~ 오늘.
  overdue: ChartSpan | null;
  schedule: PhaseSchedule;
  // 화면 톤 판정 — 끝날까지 지난 착수 지연은 LATE(scheduleToneKind).
  toneKind: PhaseScheduleKind;
  // 지금 늦어지는 공정(끝난 공정의 지난 지연은 아니다).
  delayed: boolean;
  // 차이를 보일지 — 끝난 공정이거나 지연을 세는 현장(countsPhaseDelay)일 때만.
  judged: boolean;
}

export interface ChartTick {
  col: number;
  label: string;
}

export interface PhaseChart {
  range: ChartRange | null;
  rows: ChartRow[];
  // 날짜 없는 공정 — 차트에서 빼고 개수만 알린다.
  unscheduled: number;
  todayCol: number | null;
  ticks: ChartTick[];
  holidayCols: number[];
}

export const hasPlan = <P extends ChartPhase>(p: P): p is P & { start_date: string; end_date: string } =>
  p.start_date !== null && p.end_date !== null && p.start_date <= p.end_date;

// 넘긴 꼬리를 그릴지 — 지금 늦어지는 안 끝난 공정(착수 지연 포함)이 계획 끝날을 넘겼을 때.
export const hasOverdueTail = (p: { status: ProjectPhaseStatus; end_date: string }, schedule: PhaseSchedule, today: string): boolean =>
  p.status !== "DONE" && DELAY_KINDS.includes(schedule.kind) && today > p.end_date;

// 꼬리 칸 — 문구 일수(phaseSchedule.days)와 같은 칸 수: 지연은 계획 끝날 다음 날부터, 착수 지연은 계획 시작일 다음 날부터 오늘까지.
export const overdueSpanOf = (
  p: { status: ProjectPhaseStatus; start_date: string; end_date: string },
  schedule: PhaseSchedule,
  today: string,
  range: ChartRange
): ChartSpan | null =>
  hasOverdueTail(p, schedule, today)
    ? spanOf(range, addDays(schedule.kind === "START_LATE" ? p.start_date : p.end_date, 1), today)
    : null;

export const isOngoing = (p: ChartPhase): boolean =>
  (p.status === "ACTIVE" || p.status === "HOLD") && p.actual_start_date !== null && p.actual_end_date === null;

// 실제 막대의 끝날 — 완료면 완료일, 진행·보류면 오늘(착수일보다 앞이면 착수일).
const actualEndOf = (p: ChartPhase, today: string): string | null => {
  if (p.actual_start_date === null) return null;
  if (p.actual_end_date !== null) return p.actual_end_date;
  if (!isOngoing(p)) return null;
  return today > p.actual_start_date ? today : p.actual_start_date;
};

// 차트 범위 — 계획·실제 날짜 전부를 담고, extendToToday 면 오늘까지 넓힌다(시공중 현장의 지연이 보이게).
export const phaseChartRange = (
  phases: readonly ChartPhase[],
  today: string,
  extendToToday: boolean
): ChartRange | null => {
  const planned = phases.filter(hasPlan);
  if (planned.length === 0) return null;
  const dates: string[] = [];
  for (const p of planned) {
    dates.push(p.start_date, p.end_date);
    const actualEnd = actualEndOf(p, today);
    if (p.actual_start_date !== null && actualEnd !== null && p.actual_start_date <= actualEnd) {
      dates.push(p.actual_start_date, actualEnd);
    }
  }
  if (extendToToday) dates.push(today);
  const start = minOf(dates);
  const end = maxOf(dates);
  return { start, end, days: daysBetween(start, end) + 1 };
};

// 공휴일을 받아 올 해 — 범위가 걸친 해 전부.
export const rangeYears = (range: ChartRange | null): number[] => {
  if (!range) return [];
  const years: number[] = [];
  for (let y = Number(range.start.slice(0, 4)); y <= Number(range.end.slice(0, 4)); y += 1) years.push(y);
  return years;
};

// 날짜 구간을 범위 안 칸으로 자른다 — 범위 밖이면 null.
export const spanOf = (range: ChartRange, from: string, to: string): ChartSpan | null => {
  if (to < from || to < range.start || from > range.end) return null;
  const a = Math.max(0, daysBetween(range.start, from));
  const b = Math.min(range.days - 1, daysBetween(range.start, to));
  return { from: a, span: b - a + 1 };
};

export const colOf = (range: ChartRange, date: string): number | null => {
  const col = daysBetween(range.start, date);
  return col >= 0 && col < range.days ? col : null;
};

// 눈금 — 첫날부터 한 주(석 달이 넘으면 두 주)마다 「MM-DD」.
export const ticksOf = (range: ChartRange): ChartTick[] => {
  const step = range.days > 90 ? 14 : 7;
  const ticks: ChartTick[] = [];
  for (let col = 0; col < range.days; col += step) ticks.push({ col, label: addDays(range.start, col).slice(5) });
  return ticks;
};

interface ChartInput {
  phases: readonly ChartPhase[];
  today: string;
  // 지연·차이는 스트립(progressStripOf)과 같은 기준 — countsPhaseDelay 가 아닌 현장이면 끝나지 않은 공정을 판정하지 않는다.
  projectStatus: ProjectStatus;
  extendToToday: boolean;
  holidays: ReadonlySet<string> | readonly string[];
}

export const buildPhaseChart = ({ phases, today, projectStatus, extendToToday, holidays }: ChartInput): PhaseChart => {
  const live = countsPhaseDelay(projectStatus);
  const ordered = [...phases].sort((a, b) => a.sort_order - b.sort_order || a.id.localeCompare(b.id));
  const planned = ordered.filter(hasPlan);
  const unscheduled = ordered.length - planned.length;
  const range = phaseChartRange(planned, today, extendToToday);
  if (!range) return { range: null, rows: [], unscheduled, todayCol: null, ticks: [], holidayCols: [] };

  const rows: ChartRow[] = planned.map((p) => {
    const schedule = phaseSchedule(p, today);
    const judged = p.status === "DONE" || live;
    const delayed = live && p.status !== "DONE" && DELAY_KINDS.includes(schedule.kind);
    const actualEnd = actualEndOf(p, today);
    const actual =
      p.actual_start_date !== null && actualEnd !== null ? spanOf(range, p.actual_start_date, actualEnd) : null;
    const overdue = live ? overdueSpanOf(p, schedule, today, range) : null;
    return {
      id: p.id,
      phaseCode: p.phase_code,
      status: p.status,
      startDate: p.start_date,
      endDate: p.end_date,
      // hasPlan 과 범위가 계획 날짜를 담으므로 늘 있다.
      plan: spanOf(range, p.start_date, p.end_date) ?? { from: 0, span: 1 },
      actual,
      ongoing: isOngoing(p),
      overdue,
      schedule,
      toneKind: scheduleToneKind(p, schedule, today),
      delayed,
      judged,
    };
  });

  const holidayCols = [...new Set(holidays)]
    .map((d) => colOf(range, d))
    .filter((c): c is number => c !== null)
    .sort((a, b) => a - b);

  return { range, rows, unscheduled, todayCol: colOf(range, today), ticks: ticksOf(range), holidayCols };
};

// 차트 차이 칸 — 지연은 끝날 넘긴 날(꼬리 길이와 같다) +n일 · 조기는 -n일 · 착수 지연은 시작일부터 센 날이라 타임라인·현장 차트와 같은 문구(phaseScheduleText) · 그 밖은 빈칸.
export const chartDiffText = (s: PhaseSchedule): string => {
  if (s.days <= 0) return "";
  if (s.kind === "LATE") return `+${s.days}일`;
  if (s.kind === "EARLY") return `-${s.days}일`;
  return phaseScheduleText(s);
};

// ── 진행 스트립 ──

export type StripProject = Pick<
  Project,
  "status" | "planned_start_date" | "planned_end_date" | "completion_date"
>;

export interface StripSpan {
  start: string;
  end: string;
  total: number;
  // 지난 날 수(0 ~ total).
  elapsed: number;
  // 오늘 위치 0~1 — 착공 전이면 0, 준공 예정일 뒤면 1.
  ratio: number;
}

export type StripDDay =
  | { kind: "start"; days: number }
  | { kind: "end"; days: number }
  | { kind: "completed"; date: string };

export interface ProgressStrip {
  phaseTotal: number;
  phaseDone: number;
  // 진행 — 보류는 따로 센다(phaseHold).
  phaseActive: number;
  phaseHold: number;
  // 완료 공정 비율(0~100, 반올림).
  percent: number;
  span: StripSpan | null;
  dDay: StripDDay | null;
  delayed: number;
  unscheduled: number;
}

export const progressStripOf = (
  project: StripProject,
  phases: readonly ChartPhase[],
  today: string
): ProgressStrip => {
  const phaseTotal = phases.length;
  const phaseDone = phases.filter((p) => p.status === "DONE").length;
  const phaseActive = phases.filter((p) => p.status === "ACTIVE").length;
  const phaseHold = phases.filter((p) => p.status === "HOLD").length;
  const percent = phaseTotal === 0 ? 0 : Math.round((phaseDone / phaseTotal) * 100);

  const { planned_start_date: start, planned_end_date: end } = project;
  let span: StripSpan | null = null;
  if (start !== null && end !== null && start <= end) {
    const total = daysBetween(start, end) + 1;
    const elapsed = Math.min(total, Math.max(0, daysBetween(start, today) + 1));
    span = { start, end, total, elapsed, ratio: elapsed / total };
  }

  let dDay: StripDDay | null = null;
  if (project.completion_date !== null) dDay = { kind: "completed", date: project.completion_date };
  else if (project.status === "SCHEDULED" && start !== null) dDay = { kind: "start", days: dDayOf(start, today) ?? 0 };
  else if (project.status === "IN_PROGRESS" && end !== null) dDay = { kind: "end", days: dDayOf(end, today) ?? 0 };

  return {
    phaseTotal,
    phaseDone,
    phaseActive,
    phaseHold,
    percent,
    span,
    dDay,
    delayed: countsPhaseDelay(project.status) ? delayedPhaseCount(phases, today) : 0,
    unscheduled: phases.filter((p) => !hasPlan(p)).length,
  };
};

// 「착공 D-5」 · 「준공 D-3」 · 「준공 예정일 2일 지남」 · 「준공 10-31」.
export const stripDDayText = (d: StripDDay | null): string => {
  if (d === null) return "";
  if (d.kind === "completed") return `준공 ${d.date.slice(5)}`;
  if (d.kind === "start") return d.days < 0 ? `착공 예정일 ${-d.days}일 지남` : `착공 ${dDayLabel(d.days)}`;
  return d.days < 0 ? `준공 예정일 ${-d.days}일 지남` : `준공 ${dDayLabel(d.days)}`;
};

export interface PaymentStrip {
  paid: number;
  total: number;
  // 회차 순 받음 여부 — 점 하나가 한 회차.
  dots: boolean[];
  paidLabels: string[];
  next: { label: string; due_date: string | null } | null;
}

// 스트립 대금 칸 — 회차 순으로 받은 것·다음에 받을 것.
export const paymentStripOf = (
  payments: readonly Pick<ContractPayment, "seq" | "label" | "due_date" | "paid_at">[]
): PaymentStrip => {
  const ordered = [...payments].sort((a, b) => a.seq - b.seq);
  const paid = ordered.filter((p) => p.paid_at !== null);
  const next = ordered.find((p) => p.paid_at === null);
  return {
    paid: paid.length,
    total: ordered.length,
    dots: ordered.map((p) => p.paid_at !== null),
    paidLabels: paid.map((p) => p.label),
    next: next ? { label: next.label, due_date: next.due_date } : null,
  };
};

export const paymentStripHint = (strip: PaymentStrip): string => {
  if (strip.total === 0) return "지급 회차 없음";
  const got = strip.paidLabels.length > 0 ? `${strip.paidLabels.join("·")} 받음` : "아직 받은 회차 없음";
  if (!strip.next) return `${got} · 전부 받음`;
  return `${got} · ${strip.next.label}${strip.next.due_date ? ` ${strip.next.due_date.slice(5)}` : ""}`;
};

export interface ChangeOrderStrip {
  acceptedDelta: number;
  // 고객 답변 대기(발송) 건수.
  waiting: number;
  latest: Pick<ChangeOrder, "id" | "contract_id" | "seq" | "title"> | null;
}

// 스트립 변경공사 칸 — 승인 증감은 currentTotalOf 가 낸 값을 받는다(식은 한 벌).
export const changeOrderStripOf = (
  changeOrders: readonly Pick<ChangeOrder, "id" | "contract_id" | "seq" | "title" | "status">[],
  acceptedDelta: number
): ChangeOrderStrip => {
  const live = changeOrders.filter((co) => co.status !== "VOID");
  const latest = live.reduce<(typeof live)[number] | null>((a, co) => (a === null || co.seq > a.seq ? co : a), null);
  return {
    acceptedDelta,
    waiting: changeOrders.filter((co) => co.status === "SENT").length,
    latest: latest ? { id: latest.id, contract_id: latest.contract_id, seq: latest.seq, title: latest.title } : null,
  };
};
